import {
  ArrowTopRightOnSquareIcon,
  BellAlertIcon,
  DevicePhoneMobileIcon,
  EnvelopeIcon,
  PaperAirplaneIcon,
} from '@heroicons/react/24/outline';

import AppStoreBadges from '@/components/shared/AppStoreBadges';
import Footer from '@/renderer/Footer';
import Header from '@/renderer/Header';

export default AlertsGuidePage;

// A single numbered step inside Step 1.
function Step({ n, children }: { n: number; children: React.ReactNode }) {
  return (
    <li className="flex gap-4">
      <span className="flex size-7 shrink-0 items-center justify-center rounded-full bg-emerald-600 text-sm font-semibold text-white">
        {n}
      </span>
      <div className="pt-0.5 text-sm leading-6 text-zinc-700 dark:text-zinc-300">{children}</div>
    </li>
  );
}

// The banner heading for each of the three top-level steps.
function StepHeader({
  n,
  icon: Icon,
  title,
}: {
  n: number;
  icon: React.ComponentType<{ className?: string }>;
  title: string;
}) {
  return (
    <div className="flex items-center gap-3">
      <span className="flex size-9 shrink-0 items-center justify-center rounded-full bg-emerald-600/10 text-sm font-bold text-emerald-700 dark:bg-emerald-400/10 dark:text-emerald-400">
        {n}
      </span>
      <div className="flex items-center gap-2">
        <Icon className="size-6 text-emerald-600 dark:text-emerald-400" />
        <h2 className="text-xl font-bold text-zinc-900 dark:text-zinc-100">{title}</h2>
      </div>
    </div>
  );
}

const linkCls = 'font-semibold text-emerald-600 hover:text-emerald-700 dark:text-emerald-400';
const codeCls = 'rounded bg-zinc-100 px-1 dark:bg-zinc-800';

export function AlertsGuide() {
  return (
    <div className="flex h-40 min-h-[stretch] flex-1 grow flex-col justify-between overflow-hidden">
      <Header title="Release Alert Setup" className="py-2 md:hidden" />
      <div className="overflow-auto">
        <div className="mx-auto max-w-2xl px-4 pb-24 pt-12 sm:px-6 sm:pt-20 lg:px-8">
          {/* ── Hero ── */}
          <div className="text-center">
            <h1 className="text-3xl font-bold tracking-tight text-zinc-900 sm:text-4xl dark:text-zinc-100">
              Make Telegram &amp; email alerts impossible to miss
            </h1>
            <p className="mx-auto mt-4 max-w-xl text-base leading-7 text-zinc-600 dark:text-zinc-400">
              Never miss a release. Install the RAS app for the live Release Center and push
              warnings, connect Telegram (our replacement for text alerts), add backup email
              addresses, and take a few minutes to set up your phone so RAS alerts always get
              through.
            </p>
          </div>

          {/* ── The one rule ── */}
          <div className="mt-10 rounded-xl border border-emerald-600/20 bg-emerald-50 p-5 dark:border-emerald-400/20 dark:bg-emerald-500/10">
            <div className="flex gap-3">
              <BellAlertIcon className="size-6 shrink-0 text-emerald-600 dark:text-emerald-400" />
              <div className="text-sm leading-6 text-zinc-700 dark:text-zinc-200">
                <p className="font-semibold text-zinc-900 dark:text-zinc-100">
                  Alerts tell you <em>when</em>. The Release Center is <em>where</em>.
                </p>
                <p className="mt-1">
                  We never post plays on Telegram, text, or email — those are just{' '}
                  <strong>release warnings and service updates</strong>. Every play is released only
                  in the{' '}
                  <a href="/release-center" className={linkCls}>
                    Release Center
                  </a>{' '}
                  at raspicks.com. Alerts simply let you know it&apos;s time to log in and check.
                </p>
              </div>
            </div>
          </div>

          {/* ── Get the RAS app ── */}
          <div className="mt-12 rounded-xl border border-emerald-600/20 bg-white p-6 shadow-sm dark:border-emerald-400/20 dark:bg-zinc-900/40">
            <div className="flex items-center gap-3">
              <DevicePhoneMobileIcon className="size-6 text-emerald-600 dark:text-emerald-400" />
              <h2 className="text-xl font-bold text-zinc-900 dark:text-zinc-100">
                Get the RAS app
              </h2>
            </div>
            <div className="mt-4 space-y-3 text-sm leading-6 text-zinc-700 dark:text-zinc-300">
              <p>
                The app does two things nothing else does now that text alerts are gone. First,{' '}
                <strong>push notifications</strong>: with SMS retired, push is your most reliable
                release warning, landing right on your lock screen the moment a play is coming, with
                no carrier delays or spam filters. Second, it puts the{' '}
                <a href="/release-center" className={linkCls}>
                  Release Center
                </a>{' '}
                right on your phone, updating in real time so a new play shows up the instant
                it&apos;s posted. Together with Telegram and email, push is a core part of how you
                hear a release is coming.
              </p>
              <p>
                To be clear, push handles <em>warnings</em>, not the plays themselves: every play is
                still released only in the Release Center. Push simply tells you it&apos;s time to
                open the app and check.
              </p>
              <p>
                The Android app is live on Google Play (
                <code className={codeCls}>com.raspicks.app</code>
                ). Install it, sign in, and allow notifications. (iOS is coming soon: until then,
                use Telegram and email for alerts on iPhone.)
              </p>
            </div>
            <AppStoreBadges className="mt-5" />
          </div>

          {/* ── What is Telegram ── */}
          <div className="prose prose-zinc prose-emerald mt-12 max-w-none dark:prose-invert prose-a:font-semibold prose-a:text-emerald-600 dark:prose-a:text-emerald-400">
            <h2>What is Telegram, and why do we use it?</h2>
            <p>
              Telegram is a free, secure messaging app for iPhone, Android, desktop, and the web. We
              use it as our <strong>replacement for SMS text alerts</strong>: when a play is about
              to go out, we send a short release warning to your Telegram so you&apos;re ready —
              instant and reliable, with none of the carrier delays, filtering, or fees that come
              with text messages.
            </p>
            <p>In several ways, it&apos;s actually an upgrade over texting:</p>
            <ul>
              <li>
                <strong>It works everywhere.</strong> Telegram runs over any Wi-Fi or data
                connection worldwide, so alerts keep arriving when you&apos;re traveling, out of the
                country, or somewhere with spotty cell coverage — situations where SMS often fails.
              </li>
              <li>
                <strong>It&apos;s on every screen.</strong> Telegram syncs across your phone,
                tablet, and computer at the same time. If you&apos;re at a desk during a release
                window, the alert pops up right on the machine you&apos;re already looking at.
              </li>
              <li>
                <strong>Your privacy stays yours.</strong> Connecting to our bot does{' '}
                <strong>not</strong> share your phone number with us or with anyone else, and other
                Telegram users can&apos;t see it either. You can also hide your number entirely in
                Telegram&apos;s Settings → Privacy and Security.
              </li>
            </ul>
          </div>

          {/* ── Step 1: Connect Telegram ── */}
          <div className="mt-14">
            <StepHeader n={1} icon={PaperAirplaneIcon} title="Connect Telegram" />
            <ol className="mt-6 space-y-5">
              <Step n={1}>
                <strong>Install Telegram</strong> if you don&apos;t already have it — from the{' '}
                <a
                  href="https://telegram.org/apps"
                  target="_blank"
                  rel="noreferrer"
                  className={linkCls}
                >
                  App&nbsp;Store / Google&nbsp;Play{' '}
                  <ArrowTopRightOnSquareIcon className="inline size-3.5" />
                </a>{' '}
                — and sign in with your phone number.
              </Step>
              <Step n={2}>
                Go to{' '}
                <a href="/telegram" className={linkCls}>
                  raspicks.com/telegram
                </a>{' '}
                (or open <strong>Account → Settings</strong> and find the <strong>Telegram</strong>{' '}
                section).
              </Step>
              <Step n={3}>
                Click <strong>Connect Telegram</strong>. It opens the RAS bot inside the Telegram
                app.
              </Step>
              <Step n={4}>
                In Telegram, tap <strong>Start</strong> (or <strong>Send</strong>). That links your
                Telegram to your RAS account.
              </Step>
              <Step n={5}>
                Head back to Settings — you&apos;ll see <strong>Connected</strong>. That&apos;s it.
                To link a second phone or account, use <strong>Add another</strong>; to turn alerts
                off, send <code className={codeCls}>/stop</code> to the bot or click{' '}
                <strong>Disconnect</strong>.
              </Step>
            </ol>
            <div className="mt-6">
              <a
                href="/telegram"
                className="inline-flex items-center gap-2 rounded-lg bg-emerald-600 px-4 py-2.5 text-sm font-semibold text-white shadow-sm transition hover:bg-emerald-700"
              >
                <PaperAirplaneIcon className="size-4" />
                Connect Telegram now
              </a>
            </div>
          </div>

          {/* ── Step 2: Make Telegram alerts stand out ── */}
          <div className="mt-16">
            <StepHeader n={2} icon={DevicePhoneMobileIcon} title="Make Telegram alerts stand out" />
            <p className="mt-4 text-sm leading-6 text-zinc-600 dark:text-zinc-400">
              Connecting is step one. These quick settings make RAS alerts stand out from every
              other notification on your phone — a custom sound, top-of-list placement, and alerts
              that come through even in Do Not Disturb.
            </p>

            <div className="prose prose-zinc prose-sm mt-6 max-w-none dark:prose-invert prose-a:font-semibold prose-a:text-emerald-600 dark:prose-a:text-emerald-400 prose-h3:mb-2 prose-h3:mt-8 prose-h3:text-base">
              <h3>Give RAS alerts their own sound</h3>
              <p>
                Set a unique sound for the RAS bot chat so you know it&apos;s a release warning
                without even looking at your phone.
              </p>
              <p>
                <strong>iPhone:</strong>
              </p>
              <ol>
                <li>Open your chat with the RAS bot in Telegram.</li>
                <li>Tap the bot&apos;s name at the top of the chat.</li>
                <li>
                  Tap <strong>Notifications</strong> (or the bell icon), then <strong>Sound</strong>
                  .
                </li>
                <li>
                  Pick something distinctive — you can even choose a loud, unmistakable tone you
                  never use for anything else.
                </li>
              </ol>
              <p>
                <strong>Android:</strong>
              </p>
              <ol>
                <li>Open your chat with the RAS bot.</li>
                <li>Tap the bot&apos;s name at the top.</li>
                <li>
                  Tap the <strong>bell (Notifications)</strong> icon, then{' '}
                  <strong>Customize</strong>.
                </li>
                <li>
                  Turn on <strong>Custom notifications</strong> and choose your{' '}
                  <strong>Sound</strong>.
                </li>
              </ol>

              <h3>Pin the chat to the top</h3>
              <p>
                In your Telegram chat list, long-press (or swipe on) the RAS bot chat and choose{' '}
                <strong>Pin</strong>. It stays at the top of your list so you can always find the
                latest alert in one tap.
              </p>

              <h3>Let alerts break through Do Not Disturb</h3>
              <p>
                If you use Do Not Disturb or Focus modes (nights, meetings, the gym), make an
                exception for Telegram so release warnings still come through:
              </p>
              <p>
                <strong>iPhone:</strong> Settings → <strong>Focus</strong> →{' '}
                <strong>Do Not Disturb</strong> (and any other Focus you use) →{' '}
                <strong>Apps</strong> under &ldquo;Allow Notifications&rdquo; → add{' '}
                <strong>Telegram</strong>.
              </p>
              <p>
                <strong>Android:</strong> Settings → <strong>Notifications</strong> →{' '}
                <strong>Do Not Disturb</strong> → <strong>Apps</strong> (or
                &ldquo;Exceptions&rdquo;) → add <strong>Telegram</strong>.
              </p>

              <h3>iPhone only: check Scheduled Summary</h3>
              <p>
                Apple&apos;s Scheduled Summary feature holds notifications and delivers them in
                batches at set times — which defeats the whole purpose of a release warning. Go to
                Settings → <strong>Notifications</strong> → <strong>Scheduled Summary</strong> and
                make sure <strong>Telegram</strong> (and your email app) are <strong>not</strong>{' '}
                included. Many people turned this on once and forgot it exists.
              </p>

              <h3>Android only: stop the battery saver from delaying alerts</h3>
              <p>
                Some Android phones quietly delay app notifications to save battery. To make sure
                alerts arrive instantly:
              </p>
              <ol>
                <li>
                  Settings → <strong>Apps</strong> → <strong>Telegram</strong> →{' '}
                  <strong>Battery</strong>.
                </li>
                <li>
                  Set to <strong>Unrestricted</strong> (wording varies by phone: &ldquo;Don&apos;t
                  optimize&rdquo; / &ldquo;No restrictions&rdquo;).
                </li>
              </ol>

              <h3>Make sure the bot isn&apos;t muted</h3>
              <p>
                It&apos;s easy to mute a chat by accident. Open your chat with the RAS bot — if you
                see a crossed-out bell next to the name at the top, the chat is muted. Tap the name,
                then tap <strong>Unmute</strong> (or the bell icon) to turn alerts back on.
              </p>

              <h3>Double-check the basics</h3>
              <p>
                In Telegram, go to <strong>Settings → Notifications and Sounds</strong> and confirm
                notifications for <strong>Private Chats</strong> are on. On iPhone, also check
                Settings → <strong>Notifications</strong> → <strong>Telegram</strong> and make sure{' '}
                <strong>Allow Notifications</strong>, <strong>Sounds</strong>, and{' '}
                <strong>Banners</strong> are all enabled.
              </p>

              <h3>Bonus: put Telegram on your computer</h3>
              <p>
                Install the{' '}
                <a href="https://telegram.org/apps" target="_blank" rel="noreferrer">
                  Telegram desktop app
                </a>{' '}
                or open{' '}
                <a href="https://web.telegram.org" target="_blank" rel="noreferrer">
                  web.telegram.org
                </a>{' '}
                in your browser and sign in. Alerts will pop up on your computer screen at the same
                moment they hit your phone — perfect if you&apos;re at a desk during release
                windows.
              </p>
            </div>
          </div>

          {/* ── Step 3: Email ── */}
          <div className="mt-16">
            <StepHeader n={3} icon={EnvelopeIcon} title="Make email alerts work like text alerts" />
            <p className="mt-4 text-sm leading-6 text-zinc-600 dark:text-zinc-400">
              Email is your backup layer — and it can be just as fast as texting, if your inbox
              treats RAS mail as a priority instead of burying it. A couple of minutes of setup
              fixes that permanently.
            </p>

            <div className="prose prose-zinc prose-sm mt-6 max-w-none dark:prose-invert prose-a:font-semibold prose-a:text-emerald-600 dark:prose-a:text-emerald-400 prose-h3:mb-2 prose-h3:mt-8 prose-h3:text-base">
              <h3>Add backup email addresses</h3>
              <p>
                Add a second (and third) email so a release warning never slips through — we&apos;ll
                send to all of them.
              </p>
              <ol>
                <li>
                  Open <a href="/settings">raspicks.com/settings</a> (
                  <strong>Account → Settings</strong>).
                </li>
                <li>
                  In your contact info, add a <strong>secondary</strong> (and optional{' '}
                  <strong>tertiary</strong>) email address.
                </li>
                <li>
                  Click <strong>Save</strong>. Release warnings and service updates will now go to
                  every address on your account.
                </li>
              </ol>
              <p className="not-prose mt-4">
                <a
                  href="/settings"
                  className="inline-flex items-center gap-2 rounded-lg border border-zinc-300 px-4 py-2.5 text-sm font-semibold text-zinc-700 transition hover:bg-zinc-50 dark:border-zinc-600 dark:text-zinc-200 dark:hover:bg-zinc-800"
                >
                  <EnvelopeIcon className="size-4" />
                  Go to Settings
                </a>
              </p>

              <h3>Gmail: create a &ldquo;never miss it&rdquo; filter</h3>
              <p>
                This tells Gmail our alerts are important, keeps them out of spam and the Promotions
                tab, and stars them so they jump out.
              </p>
              <ol>
                <li>
                  Open Gmail on a computer and click the <strong>search options</strong> icon
                  (sliders) on the right side of the search bar.
                </li>
                <li>
                  In the <strong>From</strong> field, enter: <strong>admin@raspicks.com</strong>{' '}
                  <em>(the address our alerts come from)</em>.
                </li>
                <li>
                  Click <strong>Create filter</strong>, then check:
                  <ul>
                    <li>
                      <strong>Never send it to Spam</strong>
                    </li>
                    <li>
                      <strong>Always mark it as important</strong>
                    </li>
                    <li>
                      <strong>Star it</strong>
                    </li>
                    <li>
                      <strong>Apply the label</strong> → create a new label called{' '}
                      <strong>RAS Alerts</strong>
                    </li>
                    <li>
                      <strong>Categorize as</strong> → <strong>Primary</strong>
                    </li>
                  </ul>
                </li>
                <li>
                  Click <strong>Create filter</strong>.
                </li>
              </ol>

              <h3>Gmail on Android: give RAS emails their own notification sound</h3>
              <ol>
                <li>
                  In the Gmail app, tap <strong>☰ → Settings</strong> → your email address.
                </li>
                <li>
                  Tap <strong>Notifications</strong> and set it to <strong>All</strong>.
                </li>
                <li>
                  Tap <strong>Manage labels</strong> → <strong>RAS Alerts</strong> (the label you
                  created above).
                </li>
                <li>
                  Set <strong>Sync messages</strong> to <strong>Last 30 days</strong>, turn on{' '}
                  <strong>Label notifications</strong>, and choose a custom <strong>Sound</strong>.
                </li>
              </ol>
              <p>Now RAS alert emails ring differently than everything else in your inbox.</p>

              <h3>iPhone: use VIP in Apple Mail for a custom alert</h3>
              <p>
                The Gmail app on iPhone doesn&apos;t support per-sender sounds, but Apple&apos;s
                built-in Mail app does, through VIP:
              </p>
              <ol>
                <li>
                  Make sure your email account is added to the iPhone <strong>Mail</strong> app
                  (Settings → Mail → Accounts).
                </li>
                <li>
                  In Mail, go to your mailbox list and tap the <strong>ⓘ</strong> next to{' '}
                  <strong>VIP</strong>.
                </li>
                <li>
                  Tap <strong>Add VIP</strong> and add <strong>admin@raspicks.com</strong> (save us
                  as a contact first if needed).
                </li>
                <li>
                  Tap <strong>VIP Alerts</strong> and choose a custom <strong>Sound</strong> and
                  alert style.
                </li>
              </ol>

              <h3>Using Outlook, Yahoo, iCloud, or AOL?</h3>
              <p>
                The same idea works everywhere — tell your provider our mail is safe and important:
              </p>
              <ul>
                <li>
                  <strong>Outlook / Hotmail:</strong> Settings → Mail → <strong>Junk email</strong>{' '}
                  → add <strong>admin@raspicks.com</strong> to <strong>Safe senders</strong>. You
                  can also create a rule to flag or categorize our messages.
                </li>
                <li>
                  <strong>Yahoo Mail:</strong> Settings → <strong>More Settings</strong> →{' '}
                  <strong>Filters</strong> → create a filter for our address that sends it straight
                  to your inbox (or a dedicated folder).
                </li>
                <li>
                  <strong>iCloud Mail:</strong> At icloud.com, open Mail → Settings (gear) →{' '}
                  <strong>Rules</strong> → create a rule for messages from our address.
                </li>
                <li>
                  <strong>AOL:</strong> Add <strong>admin@raspicks.com</strong> to your Contacts —
                  AOL prioritizes mail from saved contacts.
                </li>
              </ul>

              <h3>On your computer? Turn on desktop notifications</h3>
              <p>
                If you&apos;re at a desk during release windows: in Gmail on the web, go to{' '}
                <strong>
                  Settings (gear) → See all settings → General → Desktop notifications
                </strong>{' '}
                → turn on <strong>New mail notifications</strong> and click Save. You&apos;ll get a
                pop-up the moment an alert lands.
              </p>

              <h3>One last deliverability tip</h3>
              <p>
                Whatever email you use, add <strong>admin@raspicks.com</strong> to your contacts /
                address book. Mail from saved contacts is far less likely to ever be filtered.
              </p>
            </div>
          </div>

          {/* ── Recommended setup ── */}
          <div className="mt-16 rounded-xl border border-emerald-600/20 bg-emerald-50 p-5 dark:border-emerald-400/20 dark:bg-emerald-500/10">
            <h2 className="text-base font-bold text-zinc-900 dark:text-zinc-100">
              Recommended setup (the short version)
            </h2>
            <p className="mt-2 text-sm leading-6 text-zinc-700 dark:text-zinc-300">
              For a true text-message replacement, we suggest all four layers:
            </p>
            <ol className="mt-4 space-y-3">
              <Step n={1}>
                <strong>The RAS app with notifications allowed</strong>: push warnings the moment a
                play is coming, plus the live Release Center on your phone.
              </Step>
              <Step n={2}>
                <strong>Telegram with a custom sound + Do Not Disturb exception</strong> — your
                instant, primary alert.
              </Step>
              <Step n={3}>
                <strong>Gmail filter + custom email sound</strong> — your backup if Telegram is ever
                closed or offline.
              </Step>
              <Step n={4}>
                <strong>A second email address on your account</strong> — belt and suspenders.
              </Step>
            </ol>
            <p className="mt-4 text-sm font-medium leading-6 text-zinc-800 dark:text-zinc-200">
              Set it up once, and you&apos;ll never miss a release again.
            </p>
          </div>

          {/* ── Troubleshooting ── */}
          <div className="mt-14">
            <h2 className="text-xl font-bold text-zinc-900 dark:text-zinc-100">
              Not getting alerts? Check these three things
            </h2>
            <ol className="mt-6 space-y-5">
              <Step n={1}>
                <strong>Is the bot muted?</strong> Open the RAS chat in Telegram — a crossed-out
                bell next to the name means it&apos;s muted. Tap to unmute.
              </Step>
              <Step n={2}>
                <strong>Is Telegram allowed through Do Not Disturb?</strong> Check your Focus/DND
                exceptions (see Step 2 above). On iPhone, also make sure Telegram isn&apos;t in
                Scheduled Summary.
              </Step>
              <Step n={3}>
                <strong>
                  Are notifications enabled for Telegram in your phone&apos;s settings?
                </strong>{' '}
                iPhone: Settings → Notifications → Telegram. Android: Settings → Apps → Telegram →
                Notifications (and set Battery to Unrestricted).
              </Step>
            </ol>
            <p className="mt-6 text-sm leading-6 text-zinc-600 dark:text-zinc-400">
              Still stuck? We&apos;re happy to walk you through it — reach us on{' '}
              <a href="/support-chat" className={linkCls}>
                Support Chat
              </a>
              .
            </p>
          </div>

          {/* ── Recap ── */}
          <div className="mt-14 rounded-xl border border-zinc-950/10 p-5 dark:border-white/10">
            <h2 className="text-sm font-semibold text-zinc-900 dark:text-zinc-100">In short</h2>
            <ul className="mt-3 space-y-2 text-sm leading-6 text-zinc-600 dark:text-zinc-400">
              <li>
                <strong>The RAS app, Telegram &amp; email</strong> are how we tell you a release is
                coming (app push, Telegram, and email all carry the warning).
              </li>
              <li>
                <strong>Release Center (raspicks.com)</strong> — where every play always lives.
              </li>
              <li>
                <strong>Questions?</strong> Reach us on{' '}
                <a href="/support-chat" className={linkCls}>
                  Support Chat
                </a>
                .
              </li>
            </ul>
          </div>
        </div>
        <Footer />
      </div>
    </div>
  );
}

function AlertsGuidePage() {
  return <AlertsGuide />;
}
